import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  EmptyState,
  HealthIndicator,
  KanbanColumn,
  PageActionStrip,
  StatusChip,
} from '../../components/index.js';
import { RapidLineageGraph } from './RapidLeadershipUi.jsx';
import {
  ROLE_ORDER,
  RAPID_KANBAN_COLUMNS,
  actionDueState,
  buildDriverColumns,
  buildDriverNodes,
  buildLineagePreviewNodes,
  buildPhaseRail,
  deriveBowlerRows,
  findRoleData,
  formatLongDate,
  formatNumericGap,
  podDisplayName,
  roleRecommendedAction,
  slugify,
} from './rapidPageUtils.js';

export function QuickStat({ label, value, tone = 'default', footer }) {
  return (
    <div className={`rapid-stat-card tone-${tone}`}>
      <div className="rapid-stat-label">{label}</div>
      <div className="rapid-stat-value">{value}</div>
      {footer ? <div className="rapid-stat-footer">{footer}</div> : null}
    </div>
  );
}

export function SeededBanner({ banner }) {
  return (
    <div className="rapid-seed-banner" data-testid="banner-seeded-data">
      <strong>Illustrative data</strong>
      <span>{banner}</span>
    </div>
  );
}

export function ResetDialog({ open, onCancel, onConfirm, resetting }) {
  if (!open) return null;
  return (
    <div className="rapid-dialog-backdrop">
      <div
        className="rapid-dialog"
        role="dialog"
        aria-modal="true"
        data-testid="dialog-reset-confirm"
      >
        <h3>Restore baseline?</h3>
        <p>This restores the baseline and discards your edits — continue?</p>
        <div className="rapid-dialog-actions">
          <button className="btn ghost sm" onClick={onCancel} type="button">
            Cancel
          </button>
          <button className="btn primary sm" onClick={onConfirm} type="button" disabled={resetting}>
            {resetting ? 'Resetting…' : 'Reset workspace'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function GuidedTourPanel({ guidedTour, tourState, onBack, onNext, onSkip, onRestart }) {
  if (!tourState.open) return null;
  const step = guidedTour?.steps?.find((entry) => entry.n === tourState.step);
  if (!step) return null;
  const total = guidedTour.steps.length;
  return (
    <aside className="rapid-tour-panel" data-testid="guided-tour-panel">
      <div className="rapid-tour-kicker">Start Here Tour</div>
      <h3 data-testid={`guided-tour-step-${tourState.step}`}>
        {tourState.step}. {step.screen}
      </h3>
      <p>{step.say}</p>
      <div className="rapid-tour-meta">
        <span>
          {tourState.step} / {total}
        </span>
        <span>{guidedTour.requiresLiveAI ? 'Operator-only' : 'No live AI required'}</span>
      </div>
      <div className="rapid-tour-actions">
        <button
          className="btn ghost sm"
          data-testid="button-tour-back"
          type="button"
          onClick={onBack}
          disabled={tourState.step === 1}
        >
          Back
        </button>
        <button
          className="btn ghost sm"
          data-testid="button-tour-skip"
          type="button"
          onClick={onSkip}
        >
          Skip
        </button>
        <button
          className="btn ghost sm"
          data-testid="button-tour-restart"
          type="button"
          onClick={onRestart}
        >
          Restart
        </button>
        <button
          className="btn primary sm"
          data-testid="button-tour-next"
          type="button"
          onClick={onNext}
        >
          {tourState.step === total ? 'Finish' : 'Next'}
        </button>
      </div>
      {tourState.step === total && guidedTour.closingNarrative ? (
        <p className="rapid-tour-closing">{guidedTour.closingNarrative}</p>
      ) : null}
    </aside>
  );
}

function RoleLensSelector({ roleViews, roleLens, onChange }) {
  const roleData = findRoleData(roleViews, roleLens);
  const priorityText = roleRecommendedAction(roleData) || 'No role guidance available.';
  return (
    <div className="rapid-role-lens" data-testid="role-lens-selector">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Role Priorities</div>
          <h2>What each stakeholder is tracking on this RAPID</h2>
        </div>
      </div>
      <div className="rapid-role-pills">
        {ROLE_ORDER.map((role) => (
          <button
            key={role}
            className={`btn ${roleLens === role ? 'primary' : 'ghost'} sm`}
            data-testid={`role-lens-${slugify(role)}`}
            type="button"
            onClick={() => onChange(role)}
          >
            {role}
          </button>
        ))}
      </div>
      <div className="rapid-role-summary">
        {(roleData?.topCards || []).map((card) => (
          <span className="chip outline sm" key={card}>
            {card}
          </span>
        ))}
        <p className="rapid-role-priority">
          <span className="rapid-role-priority-label">Priority for this role</span>
          {priorityText}
        </p>
      </div>
    </div>
  );
}

export function RapidStateStrip({
  modifiedLabel,
  onReset,
  onStartTour,
  tourOpen,
  roleLens,
  roleViews,
  onRoleLensChange,
}) {
  return (
    <>
      <PageActionStrip
        filters={
          <span className="chip outline sm" data-testid="indicator-seed-state">
            {modifiedLabel}
          </span>
        }
        actions={
          <>
            <button className="btn ghost sm" type="button" onClick={onStartTour}>
              {tourOpen ? 'Restart tour' : 'Start Here tour'}
            </button>
            <button
              className="btn primary sm"
              data-testid="button-demo-reset"
              type="button"
              onClick={onReset}
            >
              Reset workspace
            </button>
          </>
        }
      />
      <RoleLensSelector roleViews={roleViews} roleLens={roleLens} onChange={onRoleLensChange} />
    </>
  );
}

export function CommandCenterTable({
  rapids,
  totalRapids,
  onClear,
  filterCategory,
  setFilterCategory,
  sortKey,
  setSortKey,
  search,
  setSearch,
}) {
  return (
    <section className="rapid-panel">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Primary story</div>
          <h2>One active RAPID and one routed no-go proof</h2>
        </div>
        <span className="chip outline sm" data-testid="rapid-authoritative-total">
          {totalRapids} total
        </span>
      </div>
      <div className="rapid-toolbar">
        <label>
          Category
          <select
            data-testid="filter-cmd-category"
            value={filterCategory}
            onChange={(event) => setFilterCategory(event.target.value)}
          >
            <option value="All">All</option>
            <option value="Active">Active</option>
            <option value="Routed/Deferred">Routed/Deferred</option>
          </select>
        </label>
        <label>
          Sort
          <select
            data-testid="sort-cmd-health"
            value={sortKey}
            onChange={(event) => setSortKey(event.target.value)}
          >
            <option value="priority">Priority</option>
            <option value="health">Health</option>
          </select>
        </label>
        <label className="rapid-toolbar-search">
          Search
          <input
            data-testid="search-global"
            placeholder="Search RAPIDs"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <button className="btn ghost sm" data-testid="filter-clear" type="button" onClick={onClear}>
          Clear filters
        </button>
      </div>
      <div className="rapid-table-scroll">
        <table className="table rapid-table-card">
          <thead>
            <tr>
              <th>RAPID</th>
              <th>Category</th>
              <th>Score</th>
              <th>Health</th>
              <th>Clock</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {rapids.map((rapid) => (
              <tr key={rapid.id} data-testid={`rapid-card-${rapid.id}`}>
                <td>
                  <Link to={`/rapids/${rapid.id}`}>{rapid.title}</Link>
                  <div className="muted small">{rapid.id}</div>
                  <div className="muted small">{rapid.summary}</div>
                </td>
                <td>
                  <StatusChip status={rapid.visibleCategory}>{rapid.visibleCategory}</StatusChip>
                </td>
                <td>
                  {rapid.scoreTotal?.toFixed ? rapid.scoreTotal.toFixed(1) : rapid.scoreTotal}
                </td>
                <td>
                  <HealthIndicator health={rapid.healthOverall} />
                </td>
                <td>
                  {rapid.timeline?.daysElapsed ?? '—'} / {rapid.timeline?.daysRemaining ?? '—'} /{' '}
                  {rapid.timeline?.totalClockDays ?? '—'}
                </td>
                <td>{rapid.valueSummary?.displayValue || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rapids.length === 0 ? (
        <EmptyState
          title="No RAPIDs match"
          description="Clear filters or search to return to the full portfolio."
        />
      ) : null}
    </section>
  );
}

export function RapidPhaseRail({ rapid }) {
  const lanes = buildPhaseRail(rapid);

  return (
    <section className="rapid-panel rapid-phase-rail-panel">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Six-week timeline</div>
          <h3>Where we are in the RAPID box</h3>
          <p>
            Current week {rapid.timeline?.currentWeek}; {rapid.timeline?.daysRemaining} days
            remaining
          </p>
        </div>
        <div className="rapid-chip-row">
          <HealthIndicator health={rapid.healthOverall} />
          <span className="chip outline sm">
            Week {rapid.timeline?.currentWeek} of {rapid.timeline?.totalWeeks}
          </span>
        </div>
      </div>
      <div
        className="rapid-phase-rail"
        data-testid="rapid-phase-rail"
        role="list"
        aria-label="RAPID phase rail"
        tabIndex={0}
      >
        <span className="sr-only">
          {rapid.timeline?.daysRemaining} days remaining. Current week {rapid.timeline?.currentWeek}
          .
        </span>
        {lanes.map((lane) => (
          <article
            key={lane.phaseId}
            className={`rapid-phase-step ${lane.state}`}
            data-testid={`rapid-phase-lane-${lane.phaseId}`}
            role="listitem"
          >
            <div className="eyebrow">{lane.label}</div>
            <strong>{lane.label}</strong>
            <span className="rapid-phase-status">
              {lane.state === 'active'
                ? `Active • Current week ${rapid.timeline?.currentWeek}`
                : lane.state === 'complete'
                  ? 'Complete'
                  : 'Upcoming'}
            </span>
            {lane.state === 'active' ? (
              <span className="rapid-phase-active" data-testid="rapid-phase-active">
                {lane.label}
              </span>
            ) : null}
          </article>
        ))}
      </div>
    </section>
  );
}

function RapidActionCard({ action, rapid }) {
  const dueState = actionDueState(action, rapid);

  return (
    <article
      className={`rapid-action-card rapid-action-card--${dueState.tone}`}
      data-testid={`action-${action.id}`}
    >
      <div className="rapid-action-card__top">
        <strong>{action.id}</strong>
        <StatusChip status={action.status}>{action.displayStatus || action.status}</StatusChip>
      </div>
      <div className="rapid-action-card__title">{action.title}</div>
      <div className="muted small">
        {podDisplayName(action.podId)} • {action.ownerLabel}
      </div>
      <div className="muted small">
        Due {formatLongDate(action.dueDate)} • {dueState.label}
      </div>
      {action.blockedReason ? (
        <p className="rapid-action-card__note">{action.blockedReason}</p>
      ) : null}
    </article>
  );
}

export function RapidActionsBoard({ actions, rapid }) {
  return (
    <div className="rapid-actions-board" data-testid="rapid-actions-kanban">
      {RAPID_KANBAN_COLUMNS.map(([columnId, label]) => {
        const items = actions.filter((action) => action.kanbanColumn === columnId);
        return (
          <section
            className="rapid-kanban-column"
            data-testid={`kanban-column-${columnId}`}
            key={columnId}
          >
            <KanbanColumn title={label} items={items}>
              {items.length ? (
                items.map((action) => (
                  <RapidActionCard action={action} key={action.id} rapid={rapid} />
                ))
              ) : (
                <div className="rapid-empty-inline">No actions in this lane.</div>
              )}
            </KanbanColumn>
          </section>
        );
      })}
    </div>
  );
}

export function RapidPoamTable({ actions, rapid }) {
  return (
    <section className="rapid-panel">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">POA&amp;M</div>
          <h3>Execution tracker</h3>
        </div>
      </div>
      <div className="rapid-table-scroll rapid-scroll-x" aria-label="POA&M table" tabIndex={0}>
        <table className="table rapid-poam-table" data-testid="rapid-poam-table">
          <thead>
            <tr>
              <th>Action</th>
              <th>Pod</th>
              <th>Owner</th>
              <th>Status</th>
              <th>% Complete</th>
              <th>Due Date</th>
              <th>Health</th>
            </tr>
          </thead>
          <tbody>
            {actions.map((action) => {
              const dueState = actionDueState(action, rapid);
              return (
                <tr data-testid={`poam-row-${action.id}`} key={action.id}>
                  <td>
                    <strong>{action.id}</strong>
                    <div>{action.title}</div>
                  </td>
                  <td>{podDisplayName(action.podId)}</td>
                  <td>{action.ownerLabel}</td>
                  <td>{action.displayStatus || action.status}</td>
                  <td>{action.percentComplete ?? 0}%</td>
                  <td>{formatLongDate(action.dueDate)}</td>
                  <td>
                    <span className={`rapid-health-pill rapid-health-pill--${dueState.tone}`}>
                      {dueState.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function RapidMetricsProofTable({ metrics }) {
  const showIndicatorType = metrics.some((metric) => metric.indicatorType);

  return (
    <div
      className="rapid-table-scroll rapid-scroll-x"
      aria-label="Metrics proof table"
      tabIndex={0}
    >
      <table className="table rapid-metrics-proof-table" data-testid="rapid-metrics-proof-table">
        <thead>
          <tr>
            <th>Metric</th>
            <th>Pod</th>
            <th>Measure Type</th>
            {showIndicatorType ? <th>Indicator Type</th> : null}
            <th>Baseline</th>
            <th>Current</th>
            <th>Target</th>
            <th>Source Status</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {metrics.map((metric) => (
            <tr data-testid={`metrics-proof-row-${metric.id}`} key={metric.id}>
              <td>
                <strong data-testid={`metric-${metric.id}`}>{metric.id}</strong>
                <div>{metric.name}</div>
              </td>
              <td>{podDisplayName(metric.podId)}</td>
              <td>{metric.measureType}</td>
              {showIndicatorType ? <td>{metric.indicatorType || 'Not served'}</td> : null}
              <td>{metric.baselineValue}</td>
              <td>{metric.currentValue}</td>
              <td>{metric.targetValue}</td>
              <td>{metric.sourceStatus}</td>
              <td>
                <StatusChip status={metric.status}>{metric.status}</StatusChip>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RapidBowlerGrid({ bowlerChart }) {
  const rows = deriveBowlerRows(bowlerChart);

  return (
    <section className="rapid-panel">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Performance proof</div>
          <h3>Virtual PHA Utilization bowler chart</h3>
        </div>
      </div>
      <div className="rapid-note">
        Baseline {bowlerChart?.baseline} • Current {bowlerChart?.current} • Target{' '}
        {bowlerChart?.fy26Target}
      </div>
      {rows.length ? (
        <div
          className="rapid-table-scroll rapid-scroll-x"
          aria-label="Virtual PHA Utilization bowler grid"
          tabIndex={0}
        >
          <table className="table rapid-bowler-table" data-testid="rapid-bowler-grid">
            <thead>
              <tr>
                <th>Period</th>
                <th>Actual</th>
                <th>Target</th>
                <th>Gap</th>
                <th>RAG</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  className={row.kind === 'Forecast' ? 'is-forecast' : ''}
                  data-testid={`rapid-bowler-row-${slugify(row.month)}`}
                  key={row.month}
                >
                  <td>{row.month}</td>
                  <td>{row.actual}</td>
                  <td>{row.target}</td>
                  <td>{formatNumericGap(row.gap, row.actual || row.target || '')}</td>
                  <td>
                    <StatusChip status={String(row.rag).toLowerCase()}>{row.rag}</StatusChip>
                  </td>
                  <td>{row.kind}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rapid-empty-inline">No bowler readings are available for this metric.</div>
      )}
    </section>
  );
}

export function RapidDriverDiagram({ detail, onOpenEvidence }) {
  const eligiblePods = (detail?.pods || []).filter((pod) => ['POD-1', 'POD-2'].includes(pod.id));
  const [selectedPodId, setSelectedPodId] = useState(eligiblePods[0]?.id || null);
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const surfaceRef = useRef(null);
  const columnsRef = useRef(null);
  const nodeRefs = useRef(new Map());
  const [connectors, setConnectors] = useState([]);

  const currentPod =
    eligiblePods.find((pod) => pod.id === selectedPodId) || eligiblePods[0] || null;
  const driverNodes = buildDriverNodes(currentPod);
  const currentDetail =
    (currentPod?.driverDetails || []).find((entry) => entry.nodeId === selectedDriverId) || null;
  const selectedNode = driverNodes.find((node) => node.id === selectedDriverId) || null;
  const columns = buildDriverColumns(currentPod);
  const fallbackConnectors = driverNodes
    .filter((node) => node.parentId)
    .map((node, index) => ({
      id: `${node.parentId}-${node.id}`,
      d: `M 8 ${index * 16 + 12} C 48 ${index * 16 + 12}, 72 ${index * 16 + 12}, 112 ${index * 16 + 12}`,
    }));

  useEffect(() => {
    if (!currentPod) return;
    if (!selectedDriverId || !driverNodes.some((node) => node.id === selectedDriverId)) {
      setSelectedDriverId(driverNodes[0]?.id || null);
    }
  }, [currentPod, driverNodes, selectedDriverId]);

  useEffect(() => {
    if (!currentPod) {
      setConnectors([]);
      return undefined;
    }

    function updateConnectors() {
      const base = columnsRef.current;
      if (!base) return;
      const baseRect = base.getBoundingClientRect();
      const next = [];

      for (const node of driverNodes) {
        if (!node.parentId) continue;
        const parentEl = nodeRefs.current.get(node.parentId);
        const childEl = nodeRefs.current.get(node.id);
        if (!parentEl || !childEl) continue;
        const parentRect = parentEl.getBoundingClientRect();
        const childRect = childEl.getBoundingClientRect();
        const startX = parentRect.right - baseRect.left;
        const startY = parentRect.top + parentRect.height / 2 - baseRect.top;
        const endX = childRect.left - baseRect.left;
        const endY = childRect.top + childRect.height / 2 - baseRect.top;
        const control = Math.max(28, (endX - startX) / 2);
        next.push({
          id: `${node.parentId}-${node.id}`,
          d: `M ${startX} ${startY} C ${startX + control} ${startY}, ${endX - control} ${endY}, ${endX} ${endY}`,
        });
      }

      setConnectors(next);
    }

    const frame = requestAnimationFrame(updateConnectors);
    window.addEventListener('resize', updateConnectors);
    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(updateConnectors);
      if (surfaceRef.current) observer.observe(surfaceRef.current);
      if (columnsRef.current) observer.observe(columnsRef.current);
    }

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', updateConnectors);
      if (observer) observer.disconnect();
    };
  }, [currentPod, driverNodes]);

  if (!currentPod) return null;

  return (
    <section className="rapid-panel rapid-driver-diagram-card" data-testid="rapid-driver-diagram">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Driver diagram</div>
          <h3 id="diagnosis-heading-driver" data-testid="diagnosis-heading-driver" tabIndex={-1}>
            Driver diagram
          </h3>
          <p>Select a driver to inspect linked metric, action, evidence, and root-cause context.</p>
        </div>
      </div>
      <div
        className="rapid-driver-diagram-tabs"
        role="tablist"
        aria-label="Driver diagram selector"
      >
        {eligiblePods.map((pod) => (
          <button
            aria-selected={pod.id === currentPod.id}
            className={`rapid-driver-tab ${pod.id === currentPod.id ? 'active' : ''}`}
            data-testid={`rapid-driver-diagram-pod-${pod.id}`}
            key={pod.id}
            role="tab"
            type="button"
            onClick={() => setSelectedPodId(pod.id)}
          >
            {podDisplayName(pod.id)}
          </button>
        ))}
      </div>
      <div className="diagnosis-driver-layout" data-testid="diagnosis-driver-workspace">
        <div className="rapid-driver-diagram-frame">
          <div className="rapid-driver-diagram-header">
            <div className="rapid-driver-badge" aria-hidden>
              DoD
            </div>
            <div>
              <div className="rapid-driver-title">{currentPod.name}</div>
              <div className="rapid-driver-subtitle">{currentPod.primaryOutcome}</div>
            </div>
            <div className="rapid-driver-header-right">
              <span className="rapid-driver-as-of">
                As of: {formatLongDate(detail?.rapid?.timeline?.currentDemoDate)}
              </span>
            </div>
          </div>
          <div
            className="rapid-driver-diagram-surface"
            ref={surfaceRef}
            aria-label="Driver diagram"
            tabIndex={0}
          >
            <div
              className="rapid-driver-columns"
              ref={columnsRef}
              style={{ '--tier-count': columns.length }}
            >
              <svg aria-hidden="true" className="rapid-driver-connectors">
                {(connectors.length ? connectors : fallbackConnectors).map((connector) => (
                  <path d={connector.d} data-testid="rapid-driver-connector" key={connector.id} />
                ))}
              </svg>
              {columns.map((column) => (
                <div className="rapid-driver-column" key={column.tier}>
                  <div className="rapid-driver-column-title">{column.label}</div>
                  <div className="rapid-driver-node-stack">
                    {column.nodes.map((node) => (
                      <button
                        aria-label={`${currentPod.name} Tier ${node.tier} ${node.driver}`}
                        className={`rapid-driver-node ${selectedDriverId === node.id ? 'is-active' : ''}`}
                        data-testid={`driver-node-${node.id}`}
                        key={node.id}
                        ref={(element) => {
                          if (element) nodeRefs.current.set(node.id, element);
                          else nodeRefs.current.delete(node.id);
                        }}
                        type="button"
                        onClick={() => setSelectedDriverId(node.id)}
                      >
                        <span className="rapid-driver-node-label">{node.driver}</span>
                        <span className="rapid-driver-node-measure">
                          {node.metric || 'No linked metric yet'}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rapid-driver-footer">UNCLASSIFIED</div>
        </div>
        <section className="rapid-driver-detail" data-testid="rapid-driver-detail">
          <div className="eyebrow">Driver detail</div>
          {selectedNode ? (
            <div data-testid="rapid-driver-detail-panel">
              <div className="row spread rapid-driver-detail-head">
                <h4>{selectedNode.driver}</h4>
                <span className="chip outline sm">
                  {podDisplayName(currentPod.id)} · Tier {selectedNode.tier}
                </span>
              </div>
              <p>
                {currentDetail?.summary ||
                  selectedNode.metric ||
                  'No linked detail yet for this driver.'}
              </p>
              <ul className="rapid-driver-detail-list">
                <li data-testid="rapid-driver-detail-metric">
                  <span className="k">Metric</span>
                  <span className="v">
                    {(
                      currentDetail?.linkedMetricIds || [
                        selectedNode.metric || 'No linked metric yet',
                      ]
                    ).join(', ')}
                  </span>
                </li>
                <li data-testid="rapid-driver-detail-action">
                  <span className="k">Action</span>
                  <span className="v">
                    {(currentDetail?.linkedActionIds || ['No linked action yet']).join(', ')}
                  </span>
                </li>
                <li data-testid="rapid-driver-detail-evidence">
                  <span className="k">Evidence</span>
                  <span className="v">
                    {(currentDetail?.linkedEvidence || ['No linked evidence yet']).join(', ')}
                  </span>
                </li>
                <li data-testid="rapid-driver-detail-root-cause">
                  <span className="k">Root cause</span>
                  <span className="v">
                    {(currentDetail?.linkedRootCauseIds || ['No linked root-cause yet']).join(', ')}
                  </span>
                </li>
                <li data-testid="rapid-driver-detail-next-action">
                  <span className="k">Next action</span>
                  <span className="v">
                    {currentDetail?.linkedActionIds?.[0] || 'No next action yet.'}
                  </span>
                </li>
              </ul>
              {(currentDetail?.linkedEvidence || []).length ? (
                <button
                  className="btn ghost sm"
                  type="button"
                  onClick={() =>
                    onOpenEvidence?.({
                      title: selectedNode.driver,
                      summary:
                        currentDetail.summary || selectedNode.metric || 'Driver detail evidence',
                      source: currentDetail.linkedEvidence.join(', '),
                      owner: currentPod.name,
                    })
                  }
                >
                  Open evidence
                </button>
              ) : null}
            </div>
          ) : (
            <p>No linked detail yet for this driver.</p>
          )}
        </section>
      </div>
    </section>
  );
}

export function RapidLineagePreview({ detail, onOpenEvidence }) {
  const nodes = buildLineagePreviewNodes(detail);

  if (!nodes.length) {
    return (
      <section className="rapid-panel" data-testid="rapid-lineage-preview">
        <div className="eyebrow">Lifecycle lineage preview</div>
        <p>Lineage preview will appear when RAPID artifacts are linked.</p>
      </section>
    );
  }

  return (
    <section className="rapid-panel" data-testid="rapid-lineage-preview">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Lifecycle lineage preview</div>
          <h3>Lifecycle lineage preview</h3>
          <p>
            RAPID context carried forward from request through handoff and PRIME-derived project
            preview.
          </p>
        </div>
      </div>
      <div className="rapid-note">PRIME-derived graduation remains a preview.</div>
      <RapidLineageGraph nodes={nodes} onOpenEvidence={onOpenEvidence} />
    </section>
  );
}
